/**
 * ReviewWorkspace — Feb 2026 final-interaction closeout.
 *
 * Unified toolbar (Highlight / Bold / Colour Scale / Clear Fill) all read
 * from the SAME canonical selection object emitted by WorkbookRenderer:
 *   { ranges:[{r1,c1,r2,c2}], cells:["R{r}C{r}"], cols:[c], anchor, focus }
 *
 * Every formatting action goes through the batched
 *   POST /api/reviews/{id}/annotations/bulk
 * endpoint so applying to N selected units is exactly ONE HTTP request.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { WorkbookRenderer, HIGHLIGHT_BG } from '../components/reviews/WorkbookRenderer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  ArrowLeft, Presentation, Pencil, Check, Download,
  Palette, Bold, Plus, Maximize, X as XIcon, PaintBucket,
} from 'lucide-react';

// Palette of named light shades (matches HIGHLIGHT_BG in renderer).
const PALETTE_ORDER = [
  { v: 'yellow',     label: 'Yellow' },
  { v: 'lightgreen', label: 'Light Green' },
  { v: 'green',      label: 'Green' },
  { v: 'mint',       label: 'Mint' },
  { v: 'lightblue',  label: 'Light Blue' },
  { v: 'blue',       label: 'Blue' },
  { v: 'lavender',   label: 'Lavender' },
  { v: 'pink',       label: 'Pink' },
  { v: 'orange',     label: 'Orange' },
  { v: 'amber',      label: 'Amber' },
  { v: 'red',        label: 'Red' },
  { v: 'grey',       label: 'Grey' },
];

// Backend only accepts {green, amber, red, null} for the enum `highlight`
// field (locked by schema). All other palette entries persist through the
// `custom_fill` hex field. This keeps the store forward-compatible without
// a schema migration.
const NAMED_HIGHLIGHTS = new Set(['green', 'amber', 'red']);

function TitleEditor({ id, initialTitle, onSaved }) {
  const { API, getHeaders } = useAuth();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initialTitle);
  useEffect(() => setValue(initialTitle), [initialTitle]);
  const save = async () => {
    if (!value.trim() || value === initialTitle) { setEditing(false); return; }
    try {
      const r = await fetch(`${API}/reviews/${id}`, {
        method: 'PATCH', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: value.trim() }),
      });
      if (!r.ok) throw new Error();
      toast.success('Renamed'); onSaved?.(value.trim()); setEditing(false);
    } catch { toast.error('Failed to rename'); }
  };
  return editing ? (
    <div className="flex items-center gap-2">
      <Input value={value} onChange={(e) => setValue(e.target.value)}
             className="max-w-md h-9 text-lg font-semibold" autoFocus
             onKeyDown={(e) => e.key === 'Enter' && save()} data-testid="rw-title-input"/>
      <Button size="sm" onClick={save} data-testid="rw-title-save"><Check className="w-4 h-4"/></Button>
    </div>
  ) : (
    <div className="flex items-center gap-2">
      <h1 className="text-xl sm:text-2xl font-semibold text-[color:var(--color-text-primary)]"
          data-testid="rw-title">{initialTitle}</h1>
      <button onClick={() => setEditing(true)} className="text-[#7A8B82] hover:text-[color:var(--color-primary)]"
              data-testid="rw-title-edit"><Pencil className="w-3.5 h-3.5"/></button>
    </div>
  );
}

// Highlight palette dropdown — 12 named shades + No Fill + Custom picker.
function HighlightMenu({ onPickNamed, onPickCustom, onClearFill, disabled }) {
  const [open, setOpen] = useState(false);
  const [customVal, setCustomVal] = useState('#FFF6C2');
  return (
    <div className="relative">
      <Button size="sm" variant="outline" disabled={disabled}
              onClick={() => setOpen((v) => !v)}
              data-testid="tb-highlight-btn"
              className="h-8 text-xs gap-1">
        <PaintBucket className="w-3.5 h-3.5"/> Highlight
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)}/>
          <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-[#E7EBE6] rounded-md shadow-lg p-3 flex flex-col gap-2.5 min-w-[260px]"
               data-testid="tb-highlight-menu">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#7A8B82] mb-1.5">Palette</p>
              <div className="grid grid-cols-6 gap-1.5">
                {PALETTE_ORDER.map(({ v, label }) => (
                  <button key={v}
                          onClick={() => { onPickNamed(v); setOpen(false); }}
                          data-testid={`tb-hl-${v}`}
                          title={label}
                          className="w-8 h-8 rounded border border-[#E7EBE6] hover:scale-110 transition-transform"
                          style={{ background: HIGHLIGHT_BG[v] }}/>
                ))}
              </div>
            </div>
            <div className="border-t border-[#E7EBE6] pt-2">
              <p className="text-[10px] uppercase tracking-wider text-[#7A8B82] mb-1.5">Custom</p>
              <div className="flex items-center gap-2">
                <input type="color" value={customVal}
                       onChange={(e) => setCustomVal(e.target.value)}
                       data-testid="tb-hl-custom-input"
                       className="w-8 h-8 rounded border border-[#E7EBE6] cursor-pointer"/>
                <Button size="sm" variant="outline"
                        onClick={() => { onPickCustom(customVal); setOpen(false); }}
                        data-testid="tb-hl-custom-apply"
                        className="h-7 text-[11px]">Apply {customVal}</Button>
              </div>
            </div>
            <div className="border-t border-[#E7EBE6] pt-2 flex items-center justify-between">
              <button onClick={() => { onClearFill(); setOpen(false); }}
                      data-testid="tb-hl-clear"
                      className="text-[11px] text-[#7A8B82] hover:text-red-600 underline">
                No Fill / Clear
              </button>
              <span className="text-[10px] text-[#B4C0BB] italic">Clears fill · keeps bold + remarks</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * sprint 376 · Colour Scale menu — reviewer explicitly chooses whether
 * HIGH values are good (default; revenue-like) or LOW values are good
 * (cost-like). No auto-detection — the reviewer picks per selection.
 */
function ColourScaleMenu({ onPick, disabled }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <Button size="sm" variant="outline" disabled={disabled}
              onClick={() => setOpen(v => !v)}
              data-testid="tb-color-scale-btn"
              className="h-8 text-xs gap-1">
        <Palette className="w-3.5 h-3.5"/> Colour Scale
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)}/>
          <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-[#E7EBE6] rounded-md shadow-lg p-2 min-w-[240px]"
                data-testid="tb-color-scale-menu">
            <p className="text-[10px] uppercase tracking-wider text-[#7A8B82] px-1 pb-1.5">Direction</p>
            <button onClick={() => { onPick('high_good'); setOpen(false); }}
                     data-testid="tb-cs-high-good"
                     className="w-full text-left px-2 py-1.5 rounded hover:bg-[#F9FBF7] flex items-center gap-2">
              <div className="w-14 h-4 rounded"
                    style={{ background: 'linear-gradient(to right, rgb(249,205,199), rgb(252,231,176), rgb(193,232,197))' }}/>
              <div className="min-w-0">
                <p className="text-xs font-medium text-[#1A2421]">High is Good</p>
                <p className="text-[10px] text-[#7A8B82]">Revenue, Sales, Profit, Occupancy</p>
              </div>
            </button>
            <button onClick={() => { onPick('low_good'); setOpen(false); }}
                     data-testid="tb-cs-low-good"
                     className="w-full text-left px-2 py-1.5 rounded hover:bg-[#F9FBF7] flex items-center gap-2 mt-0.5">
              <div className="w-14 h-4 rounded"
                    style={{ background: 'linear-gradient(to right, rgb(193,232,197), rgb(252,231,176), rgb(249,205,199))' }}/>
              <div className="min-w-0">
                <p className="text-xs font-medium text-[#1A2421]">Low is Good</p>
                <p className="text-[10px] text-[#7A8B82]">Food Cost %, Expenses, Complaints</p>
              </div>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function ReviewWorkspace({ viewerMode = 'admin' } = {}) {
  const { API, getHeaders } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();

  const [review, setReview] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [sheet, setSheet] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  // Canonical selection { ranges, cells, cols, anchor, focus }
  const [selection, setSelection] = useState({ ranges: [], cells: [], cols: [], anchor: null, focus: null });
  const [showRemarks, setShowRemarks] = useState(true);
  const [prevActions, setPrevActions] = useState({ items: [], previous_review: null });

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/reviews/${id}`, { headers: getHeaders() });
      if (r.status === 403 || r.status === 404) {
        setLoadError(r.status === 403 ? 'You do not have access to this review.' : 'Review not found.');
        return;
      }
      if (!r.ok) throw new Error('Failed to load review');
      setReview(await r.json());
    } catch (e) { toast.error(String(e.message || e)); }
  }, [API, getHeaders, id]);
  useEffect(() => { load(); }, [load]);

  const loadSheet = useCallback(async (idxN) => {
    try {
      const r = await fetch(`${API}/reviews/${id}/sheets/${idxN}`, { headers: getHeaders() });
      if (!r.ok) throw new Error();
      setSheet(await r.json());
    } catch { toast.error('Failed to load sheet'); }
  }, [API, getHeaders, id]);
  useEffect(() => { loadSheet(sheetIndex); }, [sheetIndex, loadSheet]);

  const loadAnnotations = useCallback(async () => {
    try {
      const r = await fetch(`${API}/reviews/${id}/annotations`, { headers: getHeaders() });
      if (r.ok) setAnnotations((await r.json()).items || []);
    } catch { /* silent */ }
  }, [API, getHeaders, id]);
  useEffect(() => { loadAnnotations(); }, [loadAnnotations]);

  const loadPrevActions = useCallback(async () => {
    try {
      const r = await fetch(`${API}/reviews/${id}/previous-actions`, { headers: getHeaders() });
      if (r.ok) setPrevActions(await r.json());
    } catch { /* silent */ }
  }, [API, getHeaders, id]);
  useEffect(() => { loadPrevActions(); }, [loadPrevActions]);

  // ── Persistence helpers ─────────────────────────────────────────────
  const post = async (body) => {
    const r = await fetch(`${API}/reviews/${id}/annotations`, {
      method: 'POST', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error(); return r.json();
  };
  const patchAnn = async (aid, body) => {
    const r = await fetch(`${API}/reviews/${id}/annotations/${aid}`, {
      method: 'PATCH', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error(); return r.json();
  };
  const del = async (aid) => {
    const r = await fetch(`${API}/reviews/${id}/annotations/${aid}`, {
      method: 'DELETE', headers: getHeaders(),
    });
    if (!r.ok) throw new Error();
  };
  const bulk = async (ops) => {
    if (!ops.length) return { ok: true };
    const r = await fetch(`${API}/reviews/${id}/annotations/bulk`, {
      method: 'POST', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ ops }),
    });
    if (!r.ok) throw new Error();
    return r.json();
  };

  // ── Selection → targets ─────────────────────────────────────────────
  // Convert canonical selection into the list of annotation targets used
  // by the batched writer. Rules:
  //   • Each contiguous rectangular range with area > 1 → one 'range' target.
  //   • Each 1×1 range → 'cell' target.
  //   • Each element in cells[] → 'cell' target.
  //   • Each element in cols[]  → 'column' target.
  //
  // De-duplication ensures no target is written twice per action.
  const buildTargets = useCallback((sel) => {
    const list = [];
    const seen = new Set();
    const push = (type, ref) => {
      const key = `${type}:${ref}`;
      if (seen.has(key)) return;
      seen.add(key);
      list.push({ type, ref });
    };
    for (const c of (sel.cols || [])) push('column', `C${c}`);
    for (const rng of (sel.ranges || [])) {
      const isPoint = rng.r1 === rng.r2 && rng.c1 === rng.c2;
      if (isPoint) push('cell', `R${rng.r1}C${rng.c1}`);
      else push('range', `R${rng.r1}C${rng.c1}:R${rng.r2}C${rng.c2}`);
    }
    for (const cellRef of (sel.cells || [])) push('cell', cellRef);
    return list;
  }, []);

  const hasAnySelection = useMemo(() => {
    return (selection.ranges?.length || 0) > 0
        || (selection.cells?.length || 0) > 0
        || (selection.cols?.length  || 0) > 0;
  }, [selection]);

  // ── Toolbar handlers (all batched) ──────────────────────────────────
  const applyHighlight = async (namedValue) => {
    if (!hasAnySelection) return toast.error('Select cells first');
    const targets = buildTargets(selection);
    if (!targets.length) return;
    const isEnum = NAMED_HIGHLIGHTS.has(namedValue);
    const ops = targets.map(t => ({
      op: 'upsert_style',
      sheet_index: sheetIndex,
      target_type: t.type,
      target_ref: t.ref,
      ...(isEnum
          ? { highlight: namedValue }
          : { custom_fill: HIGHLIGHT_BG[namedValue] }),
    }));
    try {
      await bulk(ops);
      await loadAnnotations();
    } catch { toast.error('Failed to apply highlight'); }
  };

  const applyCustomFill = async (hex) => {
    if (!hasAnySelection) return toast.error('Select cells first');
    const targets = buildTargets(selection);
    const ops = targets.map(t => ({
      op: 'upsert_style', sheet_index: sheetIndex,
      target_type: t.type, target_ref: t.ref, custom_fill: hex,
    }));
    try { await bulk(ops); await loadAnnotations(); }
    catch { toast.error('Failed to apply fill'); }
  };

  const clearFill = async () => {
    if (!hasAnySelection) return toast.error('Select cells first');
    const targets = buildTargets(selection);
    const ops = targets.map(t => ({
      op: 'clear_fill', sheet_index: sheetIndex,
      target_type: t.type, target_ref: t.ref,
    }));
    try { await bulk(ops); await loadAnnotations(); }
    catch { toast.error('Failed to clear fill'); }
  };

  // Bold — determine "all currently bold?" across the selection, then flip.
  const toggleBold = async () => {
    if (!hasAnySelection) return toast.error('Select cells first');
    const targets = buildTargets(selection);
    const sheetAnn = annotations.filter(a => a.sheet_index === sheetIndex);
    const isBold = (t) => sheetAnn.some(a =>
      a.target_type === t.type && a.target_ref === t.ref && a.bold === true);
    const allBold = targets.every(isBold);
    const nextBold = !allBold;
    const ops = targets.map(t => ({
      op: 'upsert_style', sheet_index: sheetIndex,
      target_type: t.type, target_ref: t.ref, bold: nextBold,
    }));
    try { await bulk(ops); await loadAnnotations(); }
    catch { toast.error('Failed to toggle bold'); }
  };

  // Colour Scale — one range annotation per contiguous rectangular range in
  // the selection. Discontiguous cells / column selections are ignored (a
  // scale needs 2+ numeric cells within a shape). Manual highlight on a cell
  // still visually wins per §21 (renderer prefers explicit fill over scale).
  //
  // sprint 376 · direction: 'high_good' (revenue-like) | 'low_good' (cost-like).
  const applyColourScale = async (direction = 'high_good') => {
    const ranges = (selection.ranges || []).filter(r =>
      !(r.r1 === r.r2 && r.c1 === r.c2));
    if (!ranges.length) return toast.error('Drag-select a numeric range');
    const ops = ranges.map(r => ({
      op: 'upsert_style', sheet_index: sheetIndex,
      target_type: 'range',
      target_ref: `R${r.r1}C${r.c1}:R${r.r2}C${r.c2}`,
      color_scale: { palette: 'gyr', direction },
    }));
    try {
      await bulk(ops);
      await loadAnnotations();
      toast.success(direction === 'low_good'
        ? 'Colour scale applied (Low is Good)'
        : 'Colour scale applied (High is Good)');
    } catch { toast.error('Failed'); }
  };

  const clearColourScale = async () => {
    const rules = annotations.filter(a => a.sheet_index === sheetIndex && a.target_type === 'range' && a.color_scale);
    if (!rules.length) return toast.info('No colour scales to clear');
    try {
      await Promise.all(rules.map(r => patchAnn(r.id, { color_scale: null })));
      await loadAnnotations();
      toast.success('Cleared colour scales');
    } catch { toast.error('Failed'); }
  };

  const onSourceOverrideCommit = async (rowRef, colRef, value, existingId) => {
    const ref = `${rowRef}${colRef}`;
    try {
      if (existingId) {
        if (value === '' || value == null) await del(existingId);
        else await patchAnn(existingId, { value });
      } else if (value !== '' && value != null) {
        await post({ sheet_index: sheetIndex, target_type: 'source_override',
                      target_ref: ref, value });
      }
      loadAnnotations();
    } catch { toast.error('Failed to save edit'); }
  };

  const clearSourceOverride = async () => {
    // Only meaningful for a single source cell — find the *last* focus cell.
    const focus = selection.focus;
    if (!focus) return toast.error('Select a source cell');
    const ref = `R${focus.r}C${focus.c}`;
    const existing = annotations.find(a => a.sheet_index === sheetIndex
                                        && a.target_type === 'source_override'
                                        && a.target_ref === ref);
    if (!existing) return toast.info('No override on this cell');
    try {
      await del(existing.id);
      loadAnnotations();
      toast.success('Reverted to original value');
    } catch { toast.error('Failed'); }
  };

  const addColumn = async () => {
    const title = window.prompt('New column title', 'Action Owner');
    if (!title) return;
    let position = 'end';
    if (selection.cols?.length === 1) position = `after:C${selection.cols[0]}`;
    else if (selection.focus) position = `after:C${selection.focus.c}`;
    try {
      await post({ sheet_index: sheetIndex, target_type: 'added_column',
                    target_ref: `AC:new:${Date.now()}`, title, position });
      loadAnnotations();
    } catch { toast.error('Failed to add column'); }
  };

  const addRow = async () => {
    let position = 'end';
    if (selection.focus) position = `after:R${selection.focus.r}`;
    try {
      await post({ sheet_index: sheetIndex, target_type: 'added_row',
                    target_ref: `AR:new:${Date.now()}`, position });
      loadAnnotations();
    } catch { toast.error('Failed to add row'); }
  };

  const onRemarkCommit = async (rowRef, text) => {
    const existing = annotations.find(a => a.sheet_index === sheetIndex
                                        && a.target_type === 'row'
                                        && a.target_ref === rowRef
                                        && a.remark);
    try {
      if (existing) {
        if (text.trim() === '') await del(existing.id);
        else await patchAnn(existing.id, { remark: text });
      } else if (text.trim() !== '') {
        await post({ sheet_index: sheetIndex, target_type: 'row', target_ref: rowRef, remark: text });
      }
      loadAnnotations();
    } catch { toast.error('Failed to save remark'); }
  };

  const onCellValueCommit = async (rowRef, colRef, value, existingId) => {
    const combined = `${rowRef}${colRef}`;
    try {
      if (existingId) {
        if (value === '' || value == null) await del(existingId);
        else await patchAnn(existingId, { value });
      } else if (value !== '' && value != null) {
        await post({ sheet_index: sheetIndex, target_type: 'added_cell',
                      target_ref: combined, value });
      }
      loadAnnotations();
    } catch { toast.error('Failed to save value'); }
  };

  const onColumnWidthCommit = async (colRef, width, existingId) => {
    try {
      if (existingId) await patchAnn(existingId, { width });
      else await post({ sheet_index: sheetIndex, target_type: 'column_width',
                         target_ref: colRef, width });
      loadAnnotations();
    } catch { /* silent — width is a preference */ }
  };

  const autoFitAll = () => {
    const widths = annotations.filter(a => a.sheet_index === sheetIndex && a.target_type === 'column_width');
    Promise.all(widths.map(w => del(w.id))).then(() => loadAnnotations());
    toast.success('Reset column widths');
  };

  const downloadSource = async () => {
    try {
      const r = await fetch(`${API}/reviews/${id}/source`, { headers: getHeaders() });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = review?.original_filename || 'workbook.xlsx';
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const canApplyScale = (selection.ranges || []).some(r =>
    !(r.r1 === r.r2 && r.c1 === r.c2));
  const canClearOverride = !!(selection.focus
    && annotations.some(a => a.sheet_index === sheetIndex
      && a.target_type === 'source_override'
      && a.target_ref === `R${selection.focus.r}C${selection.focus.c}`));

  const selectionSummary = useMemo(() => {
    const parts = [];
    if (selection.ranges?.length) {
      for (const r of selection.ranges) {
        if (r.r1 === r.r2 && r.c1 === r.c2) parts.push(`R${r.r1}C${r.c1}`);
        else parts.push(`R${r.r1}C${r.c1}:R${r.r2}C${r.c2}`);
      }
    }
    if (selection.cells?.length) parts.push(`+${selection.cells.length} cell${selection.cells.length > 1 ? 's' : ''}`);
    if (selection.cols?.length)  parts.push(`${selection.cols.length} col${selection.cols.length > 1 ? 's' : ''}`);
    return parts.slice(0, 3).join(' · ') + (parts.length > 3 ? ` +${parts.length - 3}` : '');
  }, [selection]);

  if (loadError) {
    return (
      <div className="max-w-md mx-auto py-16 text-center" data-testid="review-workspace-error">
        <h2 className="text-lg font-semibold text-[color:var(--color-text-primary)] mb-2">Review unavailable</h2>
        <p className="text-sm text-[#5C6B64] mb-4">{loadError}</p>
        <Button onClick={() => navigate((viewerMode === 'manager' ? '/manager' : '/admin') + '/reviews')}
                className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white">
          Back to Reviews
        </Button>
      </div>
    );
  }
  if (!review) return <div className="p-8 text-center text-[#7A8B82]">Loading review…</div>;

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-4 flex flex-col h-[calc(100vh-90px)] bg-white"
         data-testid="review-workspace">
      <header className="flex items-start justify-between mb-3 gap-4">
        <div className="min-w-0">
          <button onClick={() => navigate((viewerMode === 'manager' ? '/manager' : '/admin') + '/reviews')}
                  className="text-xs text-[#5C6B64] hover:text-[color:var(--color-primary)] mb-1 inline-flex items-center gap-1"
                  data-testid="rw-back">
            <ArrowLeft className="w-3 h-3"/> Reviews
          </button>
          <TitleEditor id={id} initialTitle={review.title} onSaved={load}/>
          <p className="text-xs text-[#5C6B64] mt-1">
            <span className="font-semibold">{review.scope_type === 'portfolio' ? (review.portfolio_name || review.outlet_name) : review.outlet_name}</span>
            <span className="mx-1.5 text-[#B4C0BB]">·</span>
            <span className="uppercase text-[10px] tracking-wider bg-[color:var(--color-primary-tint)] text-[color:var(--color-primary-hover)] px-1.5 py-0.5 rounded mr-1">
              {review.scope_type || 'outlet'}
            </span>
            <span className="uppercase text-[10px] tracking-wider bg-[#F3F5F2] px-1.5 py-0.5 rounded">{review.period_type}</span>
            <span className="ml-1.5">{review.period_label}</span>
            <span className="mx-1.5 text-[#B4C0BB]">·</span>
            <span className="text-[10px] text-[#7A8B82]">{review.original_filename}</span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={downloadSource} data-testid="rw-download">
            <Download className="w-3.5 h-3.5 mr-1"/> Original
          </Button>
          <Button onClick={() => navigate(`/reviews/${id}/present`)}
                  data-testid="rw-present"
                  className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white gap-1.5">
            <Presentation className="w-4 h-4"/> Present
          </Button>
        </div>
      </header>

      {/* Previous Review Follow-up (only when series-linked and prior actions exist) */}
      {review.review_series_id && prevActions.previous_review && (prevActions.items.length > 0 || true) && (
        <PreviousFollowupCard prev={prevActions} reviewStatus={review.status}
                                onOpenPresent={() => navigate(`/reviews/${id}/present`)}/>
      )}
      {review.status === 'reviewed' && (
        <div className="mb-3 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-md text-xs flex items-center gap-2"
             data-testid="rw-reviewed-banner">
          <span className="text-[10px] uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 rounded font-semibold">Reviewed</span>
          <span className="text-emerald-800">
            Reviewed {review.reviewed_at ? new Date(review.reviewed_at).toLocaleDateString() : ''}
            {review.reviewed_by_email && <> by {review.reviewed_by_email}</>}
          </span>
        </div>
      )}

      {/* Sheet tabs */}
      <div className="flex items-center justify-between gap-3 mb-3" data-testid="rw-sheet-tabs">
        <div className="flex gap-1 border-b border-[#E7EBE6] flex-1">
          {(review.sheets || []).map((s) => (
            <button key={s.sheet_index}
                    onClick={() => { setSheetIndex(s.sheet_index); setSelection({ ranges: [], cells: [], cols: [], anchor: null, focus: null }); }}
                    data-testid={`rw-sheet-tab-${s.sheet_index}`}
                    className={`px-3.5 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
                      sheetIndex === s.sheet_index
                        ? 'border-[color:var(--color-primary)] text-[color:var(--color-primary-hover)]'
                        : 'border-transparent text-[#5C6B64] hover:text-[#1A2421]'}`}>
              {s.sheet_name}
            </button>
          ))}
        </div>
      </div>

      {/* Unified compact toolbar (single canonical selection engine) */}
      <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="rw-toolbar">
        <HighlightMenu
          disabled={!hasAnySelection}
          onPickNamed={applyHighlight}
          onPickCustom={applyCustomFill}
          onClearFill={clearFill}/>
        <Button size="sm" variant="outline" onClick={toggleBold} disabled={!hasAnySelection}
                data-testid="tb-bold" className="h-8 text-xs gap-1">
          <Bold className="w-3.5 h-3.5"/> Bold
        </Button>
        <ColourScaleMenu disabled={!canApplyScale} onPick={applyColourScale}/>
        <button onClick={clearColourScale} className="text-[10px] text-[#7A8B82] hover:text-red-600"
                data-testid="tb-color-scale-clear">clear scales</button>
        <span className="text-[#E7EBE6]">|</span>
        <Button size="sm" variant="outline" onClick={addRow}
                data-testid="tb-add-row" className="h-8 text-xs gap-1">
          <Plus className="w-3.5 h-3.5"/> Add Row
        </Button>
        <Button size="sm" variant="outline" onClick={addColumn}
                data-testid="tb-add-column" className="h-8 text-xs gap-1">
          <Plus className="w-3.5 h-3.5"/> Add Column
        </Button>
        <span className="text-[#E7EBE6]">|</span>
        <Button size="sm" variant="outline" onClick={autoFitAll}
                data-testid="tb-auto-fit" className="h-8 text-xs gap-1">
          <Maximize className="w-3.5 h-3.5"/> Reset Widths
        </Button>
        {canClearOverride && (
          <Button size="sm" variant="outline" onClick={clearSourceOverride}
                  data-testid="tb-revert-override" className="h-8 text-xs gap-1 text-[color:var(--color-primary-hover)]">
            Revert edit
          </Button>
        )}
        <label className="ml-2 text-xs text-[#5C6B64] flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={showRemarks} onChange={(e) => setShowRemarks(e.target.checked)}
                 data-testid="tb-show-remarks"/>
          Show Remarks
        </label>
        <div className="text-[11px] text-[#7A8B82] ml-auto" data-testid="rw-selection-info">
          {hasAnySelection ? (
            <span>Selected: {selectionSummary}</span>
          ) : (
            <span className="italic">Click · drag · Shift-click extends · Ctrl/⌘-click adds discontiguous</span>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 border border-[#E7EBE6] rounded-md bg-white overflow-hidden">
        {sheet ? (
          <WorkbookRenderer
            sheet={sheet}
            styles={review.styles || []}
            annotations={annotations}
            selection={selection}
            onSelectionChange={setSelection}
            onRemarkCommit={onRemarkCommit}
            onCellValueCommit={onCellValueCommit}
            onSourceOverrideCommit={onSourceOverrideCommit}
            onColumnWidthCommit={onColumnWidthCommit}
            showRemarks={showRemarks}/>
        ) : (
          <div className="p-8 text-center text-[#7A8B82]">Loading sheet…</div>
        )}
      </div>
    </div>
  );
}

function PreviousFollowupCard({ prev, reviewStatus, onOpenPresent }) {
  const items = prev.items || [];
  const prevReview = prev.previous_review;
  const doneCount = items.filter(a => a.status === 'done').length;
  const openCount = items.filter(a => a.status === 'open' || a.status === 'in_progress').length;
  const overdueCount = items.filter(a => {
    if (a.status === 'done' || a.status === 'dropped') return false;
    return a.due_date && a.due_date < new Date().toISOString().slice(0, 10);
  }).length;
  return (
    <div className="mb-3 border border-[color:var(--color-primary-tint)] rounded-md bg-[color:var(--color-primary-tint)]/40 overflow-hidden"
         data-testid="rw-previous-followup">
      <div className="px-3 py-2 bg-[color:var(--color-primary-tint)] flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wider bg-[color:var(--color-primary)] text-white px-1.5 py-0.5 rounded font-semibold">Previous Review</span>
        <span className="text-xs font-semibold text-[color:var(--color-primary-hover)]" data-testid="rw-prev-title">
          {prevReview?.title || 'Previous review'}
        </span>
        {prevReview?.reviewed_at && (
          <span className="text-[10px] text-[#5C6B64]">
            reviewed {new Date(prevReview.reviewed_at).toLocaleDateString()}
          </span>
        )}
        <div className="ml-auto flex items-center gap-3 text-[11px] text-[color:var(--color-primary-hover)]">
          <span data-testid="rw-prev-open"><b>{openCount}</b> Open</span>
          <span data-testid="rw-prev-done"><b>{doneCount}</b> Done</span>
          {overdueCount > 0 && <span className="text-red-600" data-testid="rw-prev-overdue"><b>{overdueCount}</b> Overdue</span>}
        </div>
      </div>
      {items.length > 0 ? (
        <div className="max-h-40 overflow-auto">
          <table className="w-full text-xs">
            <thead className="bg-white/60">
              <tr className="text-[9px] uppercase tracking-wider text-[#7A8B82]">
                <th className="text-left px-3 py-1 font-semibold">Action</th>
                <th className="text-left px-3 py-1 font-semibold">Owner</th>
                <th className="text-left px-3 py-1 font-semibold">Due</th>
                <th className="text-left px-3 py-1 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.slice(0, 8).map(a => {
                const overdue = a.due_date && a.due_date < new Date().toISOString().slice(0, 10)
                                && a.status !== 'done' && a.status !== 'dropped';
                return (
                  <tr key={a.id} className="border-t border-white/60" data-testid={`rw-prev-item-${a.id}`}>
                    <td className="px-3 py-1 text-[#1A2421]">{a.task}</td>
                    <td className="px-3 py-1 text-[#5C6B64]">{a.owner_text || '—'}</td>
                    <td className={`px-3 py-1 ${overdue ? 'text-red-600 font-semibold' : 'text-[#5C6B64]'}`}>
                      {a.due_date || '—'}
                    </td>
                    <td className="px-3 py-1 text-[10px] uppercase tracking-wider text-[#5C6B64]">{(a.status || '').replace('_', ' ')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="px-3 py-2 text-[11px] text-[#5C6B64] italic">No unresolved actions from previous review.</p>
      )}
      {reviewStatus !== 'reviewed' && items.length > 0 && (
        <div className="px-3 py-1.5 border-t border-white/60 flex items-center justify-end">
          <button onClick={onOpenPresent} data-testid="rw-review-prev-actions"
                  className="text-[11px] text-[color:var(--color-primary-hover)] hover:underline">
            Open in Present Mode → Previous Actions →
          </button>
        </div>
      )}
    </div>
  );
}
